import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { ImageSquare } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

type MediaDropzoneProps = {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
};

const ACCEPTED_FILE_TYPES = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/svg+xml': ['.svg'],
  'image/webp': ['.webp'],
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export const MediaDropzone = ({ onFilesSelected, disabled }: MediaDropzoneProps) => {
  const { toast } = useToast();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      onFilesSelected(acceptedFiles);
    },
    [onFilesSelected]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    disabled,
    onDropRejected: (fileRejections) => {
      const firstError = fileRejections[0]?.errors[0];
      if (firstError?.code === 'file-too-large') {
        toast({
          variant: 'destructive',
          title: 'File too large',
          description: 'Images must be 10 MB or smaller.',
        });
      } else if (firstError?.code === 'file-invalid-type') {
        toast({
          variant: 'destructive',
          title: 'Invalid file format',
          description: 'Only PNG, JPEG, GIF, SVG, and WEBP files are supported.',
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Upload rejected',
          description: firstError?.message || 'File cannot be uploaded.',
        });
      }
    },
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-[12px] border-2 border-dashed border-[#CECECE] bg-[#FAFAFA] px-5 py-6 text-center select-none cursor-pointer transition-colors',
        isDragActive && 'border-[#0066B2] bg-[#E6F0F8]',
        disabled && 'pointer-events-none opacity-60'
      )}
    >
      <input {...getInputProps()} />

      <ImageSquare className="h-6 w-6 text-[#707070]" weight="regular" />
      <p className="text-[14px] text-[#666666]">
        <span className="font-medium text-[#1B2021]">Drag and drop images here</span>, or use
        Upload above.
      </p>
      <p className="text-[12px] text-[#707070]">
        PNG, JPEG, GIF, SVG or WEBP · up to 10 MB each
      </p>
    </div>
  );
};
